/**
 * Модалка «Поля заявки» позиции справочника экологии (ecology-order-fields-admin): реквизиты
 * площадки, которые мастер заказа спрашивает у клиента по этой услуге (файл отдела экологии
 * «ЛК страница заказа», 28.09.2026: «У каждой площадки есть реквизит…»). Стартовый набор задан по
 * файлу; уточнения эколога администратор вносит здесь, без релиза.
 *
 * Поле: подпись, подсказка клиенту, тип (число, выбор нескольких вариантов, отходы по ФККО, файлы),
 * варианты (у выбора — по одному на строку), обязательность и порядок. Тип сохранённого поля не
 * меняется — на нём держатся значения заявок. Удалённое поле уносит значения из черновиков; у
 * поданных заказов остаётся их «фотография». Настройка сохраняется целиком одним запросом.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import { adminApi, type EcoCatalogItem, type EcoOrderField } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const FIELD_TYPE_LABELS: Record<EcoOrderField['type'], string> = {
  number: 'Целое число',
  choice_multi: 'Выбор нескольких вариантов',
  fkko_list: 'Отходы по ФККО (код необязателен)',
  file_multi: 'Файлы (несколько)',
}

/** Больше не принимает сервер (`domain.NormalizeEcoOrderFieldsConfig`). */
const MAX_FIELDS = 10
const MAX_OPTIONS = 20

const TEXTAREA_CLASS =
  'w-full rounded-8 border bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

/** Черновик поля; `uid` — ключ строки формы, `id` — у сохранённого поля; варианты — текстом. */
interface FieldDraft {
  uid: string
  id?: string
  label: string
  hint: string
  type: EcoOrderField['type']
  options: string
  required: boolean
}

let draftSeq = 0
const nextUid = () => `new-${++draftSeq}`

function draftsFrom(item: EcoCatalogItem): FieldDraft[] {
  return item.orderFields.map((f) => ({
    uid: f.id,
    id: f.id,
    label: f.label,
    hint: f.hint,
    type: f.type,
    options: f.options.join('\n'),
    required: f.required,
  }))
}

/** Варианты из текста: по строке, без пустых и повторов (как сохранит сервер). */
function optionsOf(text: string): string[] {
  const out: string[] = []
  for (const line of text.split('\n')) {
    const o = line.trim().replace(/\s+/g, ' ')
    if (o && !out.includes(o)) out.push(o)
  }
  return out
}

export function OrderFieldsDialog({
  open,
  item,
  onClose,
  onSaved,
}: {
  open: boolean
  item: EcoCatalogItem | null
  onClose: () => void
  onSaved: (item: EcoCatalogItem) => void
}) {
  const formId = useId()
  const [fields, setFields] = useState<FieldDraft[]>([])
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !item) return
    setFields(draftsFrom(item))
    setShowErrors(false)
    setError(null)
  }, [open, item])

  if (!item) return null
  const removed = item.orderFields.filter((f) => !fields.some((d) => d.id === f.id))

  const labelErrors: Record<string, string> = {}
  const optionErrors: Record<string, string> = {}
  for (const f of fields) {
    if (!f.label.trim()) labelErrors[f.uid] = 'Укажите подпись поля'
    if (f.type === 'choice_multi') {
      const n = optionsOf(f.options).length
      if (n === 0) optionErrors[f.uid] = 'Укажите варианты — по одному на строку'
      else if (n > MAX_OPTIONS) optionErrors[f.uid] = `Не больше ${MAX_OPTIONS} вариантов`
    }
  }
  const tooMany = fields.length > MAX_FIELDS
  const hasErrors =
    Object.keys(labelErrors).length > 0 || Object.keys(optionErrors).length > 0 || tooMany

  const patchField = (uid: string, next: Partial<FieldDraft>) =>
    setFields((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...next } : f)))
  const moveField = (index: number, delta: number) =>
    setFields((prev) => {
      const next = [...prev]
      const [f] = next.splice(index, 1)
      next.splice(index + delta, 0, f)
      return next
    })

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving || !item) return
    if (hasErrors) {
      setShowErrors(true)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const saved = await adminApi.ecology.saveOrderFields(item.id, {
        fields: fields.map((f) => ({
          ...(f.id ? { id: f.id } : {}),
          label: f.label.trim(),
          hint: f.hint.trim(),
          type: f.type,
          options: f.type === 'choice_multi' ? optionsOf(f.options) : [],
          required: f.required,
        })),
      })
      onSaved(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить поля заявки'))
    } finally {
      setSaving(false)
    }
  }

  const title = `Поля заявки — ${item.name}`
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            <p className="text-13 text-txt-secondary">
              Реквизиты площадки, которые мастер заказа спрашивает у клиента по этой услуге, — под
              каждой отмеченной площадкой, в этом порядке. Изменения видны в новых и неподанных
              заявках сразу; у поданных заказов остаётся их «фотография».
            </p>
            {error ? (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            ) : null}

            {fields.length === 0 ? (
              <p className="mt-4 text-13 text-txt-secondary">
                Полей заявки нет — мастер спрашивает только площадки.
              </p>
            ) : (
              <ol className="mt-4 flex flex-col gap-3">
                {fields.map((f, i) => (
                  <li key={f.uid} className="rounded-10 border border-line p-3">
                    <div className="flex items-start gap-2">
                      <span className="mt-2 text-13 text-txt-tertiary">{i + 1}.</span>
                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-3 nw:grid-cols-2">
                        <Field
                          label="Подпись"
                          required
                          error={showErrors ? labelErrors[f.uid] : undefined}
                          className="my-1"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              aria-label={`Подпись поля ${i + 1}`}
                              value={f.label}
                              invalid={showErrors && Boolean(labelErrors[f.uid])}
                              onChange={(e) => patchField(f.uid, { label: e.target.value })}
                            />
                          )}
                        </Field>
                        <Field label="Тип" className="my-1">
                          {(props) => (
                            <Select
                              value={f.type}
                              disabled={Boolean(f.id)}
                              onValueChange={(v) =>
                                patchField(f.uid, { type: v as EcoOrderField['type'] })
                              }
                            >
                              <SelectTrigger
                                id={props.id}
                                aria-label={`Тип поля ${i + 1}`}
                                className="text-left"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(Object.keys(FIELD_TYPE_LABELS) as EcoOrderField['type'][]).map(
                                  (t) => (
                                    <SelectItem key={t} value={t}>
                                      {FIELD_TYPE_LABELS[t]}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </Field>
                        <Field
                          label="Подсказка клиенту"
                          hint="Например, «Можно не заполнять, если количество не известно». Необязательно"
                          className="my-1 nw:col-span-2"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              aria-label={`Подсказка поля ${i + 1}`}
                              value={f.hint}
                              onChange={(e) => patchField(f.uid, { hint: e.target.value })}
                            />
                          )}
                        </Field>
                        {f.type === 'choice_multi' ? (
                          <Field
                            label="Варианты"
                            hint="По одному на строку, до 20. Убранный вариант исчезнет и из неподанных заявок"
                            required
                            error={showErrors ? optionErrors[f.uid] : undefined}
                            className="my-1 nw:col-span-2"
                          >
                            {(props) => (
                              <textarea
                                {...props}
                                aria-label={`Варианты поля ${i + 1}`}
                                rows={4}
                                className={`${TEXTAREA_CLASS} ${
                                  showErrors && optionErrors[f.uid]
                                    ? 'border-red'
                                    : 'border-line-strong'
                                }`}
                                value={f.options}
                                onChange={(e) => patchField(f.uid, { options: e.target.value })}
                              />
                            )}
                          </Field>
                        ) : null}
                        <div className="nw:col-span-2">
                          <LabeledCheckbox
                            checked={f.required}
                            onCheckedChange={(on) => patchField(f.uid, { required: on })}
                          >
                            Обязательное
                          </LabeledCheckbox>
                          <p className="mt-1 text-12 text-txt-tertiary">
                            Заявку не отправить, пока значение не внесено у каждой отмеченной
                            площадки.
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Поднять поле ${i + 1}`}
                          disabled={i === 0}
                          onClick={() => moveField(i, -1)}
                        >
                          <ArrowUp className="size-4 stroke-current" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Опустить поле ${i + 1}`}
                          disabled={i === fields.length - 1}
                          onClick={() => moveField(i, 1)}
                        >
                          <ArrowDown className="size-4 stroke-current" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Удалить поле ${i + 1}`}
                          onClick={() => setFields((prev) => prev.filter((d) => d.uid !== f.uid))}
                        >
                          <X className="size-4 stroke-current" aria-hidden />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {showErrors && tooMany ? (
              <p role="alert" className="mt-2 text-12 text-red">
                Не больше {MAX_FIELDS} полей заявки
              </p>
            ) : null}
            <div className="mt-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={fields.length >= MAX_FIELDS}
                leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                onClick={() =>
                  setFields((prev) => [
                    ...prev,
                    {
                      uid: nextUid(),
                      label: '',
                      hint: '',
                      type: 'number',
                      options: '',
                      required: false,
                    },
                  ])
                }
              >
                Добавить поле
              </Button>
            </div>
            {removed.length > 0 ? (
              <Alert variant="danger" className="mt-4">
                При сохранении удалятся поля {removed.map((f) => `«${f.label}»`).join(', ')} — и
                значения, внесённые в неподанных заявках. У поданных заказов останется их
                «фотография»
                {removed.some((f) => f.type === 'file_multi')
                  ? '; прикреплённые файлы останутся у клиентов в «Документах»'
                  : ''}
                .
              </Alert>
            ) : null}
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
